import { z } from 'zod';
import { Uuid } from './blocks.js';
const ExpectedRevision = z.number().int().nonnegative();
const Name = z.string().trim().min(1).max(200);
const OptionalContact = z.string().trim().min(1).max(200).nullable();
const PublicPhone = OptionalContact.refine((value) => {
    if (value === null)
        return true;
    if (!/^[+\d\s().-]+$/.test(value))
        return false;
    const digitCount = value.replace(/\D/g, '').length;
    return digitCount >= 7 && digitCount <= 15;
}, { message: 'Check the phone number — use 7–15 digits' });
const OptionalNotes = z.string().trim().min(1).max(20_000).nullable();
const OptionalVisitorContext = z.string().trim().min(1).max(200).nullable().optional();
const MoneyMinor = z.number().int().nonnegative().max(2_147_483_647);
const Currency = z.string().regex(/^[A-Z]{3}$/);
export const SessionStatus = z.enum([
    'NewRequest',
    'InDiscussion',
    'Scheduled',
    'Editing',
    'Delivered',
    'Canceled',
]);
export const SESSION_STATUS_LABELS = {
    NewRequest: 'New',
    InDiscussion: 'Discussion',
    Scheduled: 'Scheduled',
    Editing: 'Editing',
    Delivered: 'Delivered',
    Canceled: 'Canceled',
};
const clientFields = {
    name: Name,
    email: z.email().nullable(),
    phone: OptionalContact,
    instagram: OptionalContact,
    notes: OptionalNotes,
};
const clientInputFields = {
    name: Name,
    email: z.email().nullable(),
    phone: OptionalContact,
    instagram: OptionalContact.optional(),
    notes: OptionalNotes,
};
export const Client = z
    .object({
    id: Uuid,
    ...clientFields,
    archivedAt: z.iso.datetime().nullable(),
    revision: ExpectedRevision,
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
})
    .strict();
export const CreateClientRequest = z.object({ id: Uuid, ...clientInputFields }).strict();
export const UpdateClientRequest = z
    .object({
    ...clientInputFields,
    archived: z.boolean(),
    expectedRevision: ExpectedRevision,
})
    .strict();
export const SessionClientLink = z
    .object({
    clientId: Uuid,
    position: z.number().int().nonnegative(),
    isPrimary: z.boolean(),
})
    .strict();
export const SessionClient = Client.pick({
    id: true,
    name: true,
    email: true,
    phone: true,
    instagram: true,
    archivedAt: true,
    revision: true,
})
    .extend({
    position: z.number().int().nonnegative(),
    isPrimary: z.boolean(),
})
    .strict();
export const SessionChecklistItem = z
    .object({
    id: Uuid,
    position: z.number().int().nonnegative(),
    title: z.string().trim().min(1).max(500),
    completed: z.boolean(),
})
    .strict();
export const ShootTransaction = z
    .object({
    id: Uuid,
    kind: z.enum(['Income', 'Expense']),
    amountMinor: MoneyMinor.refine((value) => value > 0, 'Enter an amount greater than zero'),
    date: z.iso.date(),
    description: z.string().trim().max(500),
    purpose: z.enum(['Deposit', 'Balance', 'Other']),
})
    .strict();
const ShootTransactions = z
    .array(ShootTransaction)
    .max(1000)
    .superRefine((items, context) => {
    if (new Set(items.map((item) => item.id)).size !== items.length)
        context.addIssue({ code: 'custom', message: 'Transaction IDs must be unique' });
    for (const kind of ['Income', 'Expense']) {
        if (items
            .filter((item) => item.kind === kind)
            .reduce((sum, item) => sum + item.amountMinor, 0) > 2147483647)
            context.addIssue({ code: 'custom', message: 'Transaction total is too large' });
    }
});
export const GiftContacts = z.object({ buyerId: Uuid, recipientId: Uuid.nullable() }).strict();
export const UpdateGiftContactsRequest = z
    .object({
    expectedRevision: ExpectedRevision,
    recipient: z
        .discriminatedUnion('kind', [
        z.object({ kind: z.literal('existing'), clientId: Uuid }).strict(),
        z.object({ kind: z.literal('new'), client: CreateClientRequest }).strict(),
    ])
        .nullable(),
    mainContact: z.enum(['buyer', 'recipient']),
})
    .strict()
    .refine((value) => value.mainContact !== 'recipient' || value.recipient !== null, {
    path: ['recipient'],
    message: 'Choose a recipient first',
});
const sessionFields = {
    // Optional on the wire for older API/admin builds; new API responses always include it.
    isGiftCard: z.boolean().optional(),
    title: Name,
    status: SessionStatus,
    sessionType: z.string().trim().min(1).max(100).nullable(),
    scheduledStartAt: z.iso.datetime().nullable(),
    scheduledEndAt: z.iso.datetime().nullable(),
    location: z.string().trim().min(1).max(500).nullable(),
    priceAmountMinor: MoneyMinor.nullable(),
    expensesAmountMinor: MoneyMinor,
    currency: Currency,
    notes: OptionalNotes,
    internalNotes: OptionalNotes.optional(),
    galleryUrl: z
        .string()
        .trim()
        .max(2000)
        .url()
        .refine((value) => /^https?:\/\//i.test(value), 'Use an http or https gallery link.')
        .nullable()
        .optional(),
    requestedPackageKey: z.string().trim().min(1).max(200).nullable(),
    requestedPackageTitle: z.string().trim().min(1).max(200).nullable(),
    requestedPackagePriceLabel: z.string().trim().min(1).max(200).nullable(),
    sourcePath: z.string().trim().startsWith('/').max(500).nullable(),
};
const sessionInputFields = {
    ...sessionFields,
    transactions: ShootTransactions.optional(),
    requestedPackageKey: sessionFields.requestedPackageKey.optional(),
    requestedPackageTitle: sessionFields.requestedPackageTitle.optional(),
    requestedPackagePriceLabel: sessionFields.requestedPackagePriceLabel.optional(),
    sourcePath: sessionFields.sourcePath.optional(),
};
export const CreateSessionRequest = z
    .object({
    id: Uuid,
    ...sessionInputFields,
    clients: z.array(SessionClientLink).min(1),
    checklist: z.array(SessionChecklistItem),
})
    .strict();
export const UpdateSessionRequest = z
    .object({
    ...sessionInputFields,
    clients: z.array(SessionClientLink).min(1),
    checklist: z.array(SessionChecklistItem),
    expectedRevision: ExpectedRevision,
})
    .strict();
export const Session = z
    .object({
    id: Uuid,
    ...sessionFields,
    giftContacts: GiftContacts.nullable().optional(),
    transactions: ShootTransactions.optional(),
    inquiryContext: z
        .object({
        referrerHost: z.string().nullable(),
        campaignSource: z.string().nullable(),
        campaignMedium: z.string().nullable(),
        campaignName: z.string().nullable(),
        visitorLanguage: z.string().nullable(),
        visitorTimeZone: z.string().nullable(),
        visitorDevice: z.string().nullable(),
    })
        .strict()
        .optional(),
    profitAmountMinor: z.number().int().nullable(),
    clients: z.array(SessionClient).min(1),
    checklist: z.array(SessionChecklistItem),
    revision: ExpectedRevision,
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
})
    .strict();
export const SessionSummary = Session.omit({
    checklist: true,
    notes: true,
    internalNotes: true,
}).strict();
export const ClientsQuery = z
    .object({
    archived: z.enum(['active', 'archived', 'all']).default('active'),
    query: z.string().trim().min(1).max(100).optional(),
})
    .strict();
export const SessionsQuery = z
    .object({
    status: SessionStatus.optional(),
    query: z.string().trim().min(1).max(100).optional(),
})
    .strict();
export const ClientsResponse = z.object({ clients: z.array(Client) }).strict();
export const ClientResponse = z.object({ client: Client }).strict();
export const SessionsResponse = z.object({ sessions: z.array(SessionSummary) }).strict();
export const SessionResponse = z.object({ session: Session }).strict();
export const SessionStatusHistoryEntry = z
    .object({
    id: Uuid,
    fromStatus: SessionStatus,
    toStatus: SessionStatus,
    changedAt: z.iso.datetime({ offset: true }),
})
    .strict();
export const SessionStatusHistoryResponse = z
    .object({
    entries: z.array(SessionStatusHistoryEntry),
})
    .strict();
export const CreatePublicInquiryRequest = z
    .object({
    id: Uuid,
    name: Name,
    email: z.email().nullable(),
    phone: PublicPhone,
    instagram: OptionalContact,
    message: OptionalNotes,
    packageKey: z.string().trim().min(1).max(200).nullable(),
    packageTitle: z.string().trim().min(1).max(200).nullable().optional(),
    packagePriceLabel: z.string().trim().min(1).max(200).nullable().optional(),
    sourcePath: z.string().trim().startsWith('/').max(500),
    // Context the visitor did not type. Every field is optional so that a
    // static site build published before they existed keeps validating.
    referrerHost: OptionalVisitorContext,
    campaignSource: OptionalVisitorContext,
    campaignMedium: OptionalVisitorContext,
    campaignName: OptionalVisitorContext,
    visitorLanguage: OptionalVisitorContext,
    visitorTimeZone: OptionalVisitorContext,
    visitorDevice: OptionalVisitorContext,
    website: z.string().max(200).optional(),
    turnstileToken: z.string().trim().min(1).max(4_096).nullable().optional(),
})
    .strict()
    .superRefine((value, context) => {
    if (!value.email && !value.phone && !value.instagram) {
        context.addIssue({
            code: 'custom',
            path: ['email'],
            message: 'Add an email, phone number or Instagram handle',
        });
    }
});
export const PublicInquiryResponse = z
    .object({ requestId: Uuid, accepted: z.literal(true) })
    .strict();
export const CrmRevisionConflictEnvelope = z
    .object({
    error: z
        .object({
        code: z.literal('REVISION_CONFLICT'),
        message: z.literal('The record changed since it was loaded'),
        expectedRevision: ExpectedRevision,
        currentRevision: ExpectedRevision,
        current: z.union([Client, Session]),
        submitted: z.union([UpdateClientRequest, UpdateSessionRequest]),
    })
        .strict(),
})
    .strict();
export const CrmCreateConflictErrorResponse = z
    .object({
    error: z
        .object({
        code: z.literal('CREATE_CONFLICT'),
        message: z.literal('The supplied ID already belongs to a different record'),
    })
        .strict(),
})
    .strict();
export const CrmShootDeletionPreview = z
    .object({
    revision: ExpectedRevision,
    client: z
        .object({ id: Uuid, name: Name, revision: ExpectedRevision, canDelete: z.boolean() })
        .strict(),
})
    .strict();
export const DeleteCrmShootRequest = z
    .object({
    expectedRevision: ExpectedRevision,
    deleteClientId: Uuid.nullable(),
    expectedClientRevision: ExpectedRevision.nullable(),
    confirmed: z.literal(true),
    confirmation: z.literal('DELETE'),
})
    .strict();
export const DeleteCrmShootResponse = z
    .object({ deleted: z.literal(true), deletedClientId: Uuid.nullable() })
    .strict();
export const DeleteClientRequest = z
    .object({ expectedRevision: ExpectedRevision, confirmed: z.literal(true) })
    .strict();
export const DeleteClientResponse = z.object({ deleted: z.literal(true) }).strict();
